// @ts-strict-ignore
import React from 'react'
import { useSelector } from 'react-redux'

import { getYaPayFeatureConfig } from '@/app/model/selectors/getYaPayFeatureConfig/getYaPayFeatureConfig'

import { useProductPaymentsQuery } from '@/screens/Product/hooks/use-product-payments-query'
import { useProduct } from '@/screens/Product/hooks/useProduct'
import { QuestionIcon } from '@/screens/Product/page/assets/QuestionIcon'
import { SberIcon } from '@/screens/Product/page/assets/SberIcon'
import { TbankIcon } from '@/screens/Product/page/assets/TbankIcon'
import { VsegdaDaIcon } from '@/screens/Product/page/assets/VsegdaDaIcon'
import {
	OPEN_PRODUCT_CREDIT_TAB_EVENT,
	PRODUCT_CREDIT_SECTION_ID,
	PRODUCT_INSTALLMENT_SECTION_ID
} from '@/screens/Product/page/ui/product-tabs/credit-tab/credit-tab'

import { formatPrice } from '@/shared/lib/utils/price/format-price'
import { scrollToTabSection } from '@/shared/lib/utils/scroll/scrollToTabSection'
import { Text } from '@/shared/ui'
import Flex from '@/shared/ui/Flex'
import { Popover } from '@/shared/ui/Popover'

import { LimitIcon } from '../../../../../assets/LimitIcon'

import cl from './credit-panel.module.scss'

export type CreditPanelProps = {
	price: number
	ID: number
}

const CREDIT_RATE = 0.0359
const TABS_ID_ATTR = `productTabs`

const openCreditTabSection = (sectionId: string) => {
	window.dispatchEvent(new CustomEvent(OPEN_PRODUCT_CREDIT_TAB_EVENT, { detail: sectionId }))
}

export const CreditPanel = ({ price }: CreditPanelProps): JSX.Element => {
	const onCreditClick = () => {
		openCreditTabSection(PRODUCT_CREDIT_SECTION_ID)
	}

	const onInstallmentClick = () => {
		openCreditTabSection(PRODUCT_INSTALLMENT_SECTION_ID)
	}

	const onLimitClick = () => {
		scrollToTabSection(TABS_ID_ATTR, `-tab-limit`)
	}

	const { product } = useProduct()
	const { data } = useProductPaymentsQuery(Number(product.ID))
	const { ACTIVE } = useSelector(getYaPayFeatureConfig)

	const installmentPrice = data?.data?.MIN_INSTALLMENT_PRICE
	const hasLimit = data?.data?.PAYMENTS['LIMIT']?.length > 0
	const hasCredit = data?.data?.PAYMENTS['CREDIT']?.length > 0
	const hasInstallment = data?.data?.PAYMENTS['INSTALLMENT']?.length > 0 && !!installmentPrice

	const hasSber =
		data?.data?.PAYMENTS['INSTALLMENT']?.some(
			item => item.SYSTEM === 'sberbank_pay' || item.SYSTEM === 'sberbank_online'
		) ||
		data?.data?.PAYMENTS['CREDIT']?.some(item => item.SYSTEM === 'sberbank_pay' || item.SYSTEM === 'sberbank_online')

	const hasVsegdaDA =
		(data?.data?.PAYMENTS['INSTALLMENT']?.some(
			item => item.SYSTEM === 'always_yes_limit' || item.SYSTEM === 'always_yes'
		) ||
			data?.data?.PAYMENTS['CREDIT']?.some(
				item => item.SYSTEM === 'always_yes_limit' || item.SYSTEM === 'always_yes'
			)) &&
		ACTIVE

	const hasTbank =
		(data?.data?.PAYMENTS['INSTALLMENT']?.some(item => item.SYSTEM === 't_bank') ||
			data?.data?.PAYMENTS['CREDIT']?.some(item => item.SYSTEM === 't_bank')) &&
		ACTIVE

	return (
		<article className={cl.creditPanel}>
			<div className={cl.creditPanelIconList}>
				{hasSber && <SberIcon />}
				{hasVsegdaDA && <VsegdaDaIcon />}
				{hasLimit && <LimitIcon />}
				{hasTbank && <TbankIcon />}
			</div>

			{hasLimit && (
				<Flex alignCenter style={{ marginBottom: `2px` }}>
					<Text weight="regular">
						<button type="button" onClick={onLimitClick}>
							Лимит
						</button>
						<Text as="span" weight="bold">
							&nbsp;на покупки от{` `}
							{formatPrice(price * CREDIT_RATE)}/мес
						</Text>
					</Text>
					<Popover
						overlayInnerStyle={{ maxWidth: `25rem`, padding: `21px 16px` }}
						content="Ежемесячная сумма платежа рассчитана по ставке 17,5% годовых и сроке кредита на 36 мес.">
						<div className={cl.creditPanelIconWrap}>
							<QuestionIcon />
						</div>
					</Popover>
				</Flex>
			)}

			<Flex column>
				{hasCredit && (
					<Flex alignCenter style={{ marginBottom: `2px` }}>
						<Text weight="regular">
							<button type="button" onClick={onCreditClick}>
								Кредит
							</button>
							<Text as="span">
								&nbsp;от{` `}
								{formatPrice(price * CREDIT_RATE)}/мес
							</Text>
						</Text>
						<Popover
							overlayInnerStyle={{ maxWidth: `25rem`, padding: `21px 16px` }}
							content="Ежемесячная сумма платежа рассчитана по ставке 17,5% годовых и сроке кредита на 36 мес.">
							<div className={cl.creditPanelIconWrap}>
								<QuestionIcon />
							</div>
						</Popover>
					</Flex>
				)}

				{hasInstallment && (
					<Flex alignCenter>
						<button type="button" onClick={onInstallmentClick}>
							Рассрочка
						</button>
						<Text weight="bold">
							&nbsp;от{` `}
							<Text as="span" weight="bold">
								{formatPrice(installmentPrice)}/мес
							</Text>
						</Text>
						<Popover
							overlayInnerStyle={{ maxWidth: `25rem`, padding: `21px 16px` }}
							content="Рассрочка предоставляется с небольшим удержанием, за счёт скидки продавца от 3,5% до 12% от стоимости
          товара. Общаяя сумма переплаты за рассрочку может составлять от 1% до 10% в зависимости от предложения.">
							<div className={cl.creditPanelIconWrap}>
								<QuestionIcon />
							</div>
						</Popover>
					</Flex>
				)}
			</Flex>
		</article>
	)
}
